import { unitPriceDe, uidDe, normSeats } from '../store/cartStore.js';

// Fusión de líneas de una cuenta compartida.
//
// Vive en su propio módulo y no en `tabsStore` porque la outbox también la
// necesita al drenar —hay que volver a fundir contra la versión fresca del
// servidor, no reenviar el patch viejo— y si se importaran entre sí quedaría
// un ciclo.
//
// Las líneas se identifican por `uid`, que NO cambia nunca, y no por `lineId`,
// que codifica el tiempo y los comensales. Con `lineId` mover un platillo de
// comensal se leía como "borré uno y creé otro", y eso cobraba de más:
//
//   · dos tablets moviendo el MISMO platillo a comensales distintos dejaban dos
//     platillos en la mesa, uno de ellos inexistente;
//   · una tablet moviéndolo mientras la otra bajaba la cantidad perdía la baja.
//
// Ambos se reproducen en `tabMerge.test.mjs`.

const mismoDuenio = (a, b) =>
  normSeats(a?.seats).join('-') === normSeats(b?.seats).join('-') &&
  (a?.course ?? 1) === (b?.course ?? 1);

/**
 * Junta las líneas que acabaron siendo la misma.
 *
 * Puede pasar legítimamente: esta tablet mueve su cerveza al comensal 2 y la
 * otra ya tenía una cerveza ahí. Son dos `uid` distintos con el mismo `lineId`,
 * y dos renglones idénticos en la cuenta serían un error de captura, no dos
 * cosas. Se quedan con el `uid` del primero para que la próxima fusión los siga
 * reconociendo.
 */
const juntarIguales = (items) => {
  const porLinea = new Map();
  for (const i of items) {
    const previo = porLinea.get(i.lineId);
    if (!previo) { porLinea.set(i.lineId, i); continue; }
    const quantity = previo.quantity + i.quantity;
    porLinea.set(i.lineId, {
      ...previo,
      quantity,
      fired_qty: Math.min(quantity, (previo.fired_qty ?? 0) + (i.fired_qty ?? 0)),
      total:     quantity * unitPriceDe(previo),
    });
  }
  return [...porLinea.values()];
};

// Suma la diferencia (local − base) sobre remote, línea por línea.
export const mergeTabItems = (base = [], local = [], remote = []) => {
  const byId     = new Map(remote.map((r) => [uidDe(r), { ...r }]));
  const baseMap  = new Map(base.map((i) => [uidDe(i), i]));
  const localMap = new Map(local.map((i) => [uidDe(i), i]));

  for (const uid of new Set([...baseMap.keys(), ...localMap.keys()])) {
    const b = baseMap.get(uid), l = localMap.get(uid);
    const delta = (l?.quantity ?? 0) - (b?.quantity ?? 0);
    const r = byId.get(uid);

    // De quién es y en qué tiempo va: si esta tablet lo cambió respecto de la
    // base, manda lo de esta tablet. Es el último que mueve el platillo el que
    // sabe dónde se sentó la gente.
    if (r && l && b && !mismoDuenio(l, b)) {
      r.seats  = normSeats(l.seats);
      r.course = l.course ?? 1;
      r.lineId = l.lineId;
    }

    if (delta === 0) {
      // Sin cambio de cantidad, pero "ya en cocina" sí viaja
      if (r && l && (l.fired_qty ?? 0) > (r.fired_qty ?? 0)) r.fired_qty = Math.min(l.fired_qty, r.quantity);
      continue;
    }
    if (r) {
      const q = r.quantity + delta;
      if (q <= 0) { byId.delete(uid); continue; }
      // El precio se resuelve ANTES de pisar la cantidad: cuando la línea no
      // trae `unit_price`, se reconstruye dividiendo el total entre la cantidad
      // que le corresponde, y esa es la vieja.
      const precio = unitPriceDe(r);
      r.quantity   = q;
      r.unit_price = precio;
      r.total      = q * precio;
      r.fired_qty = Math.min(q, Math.max(r.fired_qty ?? 0, l?.fired_qty ?? 0));
    } else if (delta > 0 && l) {
      const precio = unitPriceDe(l);
      byId.set(uid, { ...l, quantity: delta, unit_price: precio, total: delta * precio, fired_qty: Math.min(l.fired_qty ?? 0, delta) });
    }
    // Sin `r` y sin aumento: la otra tablet borró la línea. No se resucita.
  }

  return juntarIguales([...byId.values()]);
};
